/**
 * profileManager.js — Feature profiles для врачей разных специальностей
 * Mikhmel Cornerstone4D DICOM Viewer Pro v5
 *
 * Каждый профиль определяет:
 *  - defaultPresets: W/L пресеты, показываемые первыми
 *  - quickTools: инструменты в быстром доступе (мобильный drawer)
 *  - panels: какие вкладки видны в панелях
 *  - clinicalTools: клинические инструменты в меню
 *  - default3DPreset: 3D пресет по умолчанию
 *  - defaultViewMode: режим при загрузке серии
 */

import { getUserPref, setUserPref } from '../storage/dicomStore.js';
import { log } from '../utils/logger.js';
import { escapeHtml } from '../utils/helpers.js';

// [FIX] AbortController для очистки всех addEventListener при destroy
let _ac = new AbortController();


// ══════════════════════════════════════════════════════════════
//  ОПРЕДЕЛЕНИЯ ПРОФИЛЕЙ
// ══════════════════════════════════════════════════════════════

export const PROFILES = {
  general: {
    id: 'general',
    name: 'Общий',
    icon: '🏥',
    description: 'Все инструменты и пресеты',
    defaultPresets: ['ct_abdomen', 'ct_bone', 'ct_lung', 'ct_brain', 'ct_soft_tissue'],
    quickTools: ['WindowLevel', 'Pan', 'Zoom', 'StackScroll', 'Length', 'Angle', 'EllipticalROI'],
    panels: ['ai', 'seg', 'render', 'fusion', 'clinical', 'special', 'measure', 'report'],
    clinicalTools: ['runAgatstonScore', 'runEmphysemaQuantification', 'runCTR', 'runBMD', 'runPneumothoraxDetection', 'runCardiacSegmentation', 'runAortaDiameter', 'runFractureDetection'],
    default3DPreset: 'ct-bone-clean',
    defaultViewMode: 'mpr',
  },

  cardiology: {
    id: 'cardiology',
    name: 'Кардиология',
    icon: '❤️',
    description: 'Коронарные артерии, кальциноз, фракция выброса',
    defaultPresets: ['ct_angio', 'ct_mediastinum', 'ct_soft_tissue', 'ct_bone', 'ct_chest'],
    quickTools: ['WindowLevel', 'Pan', 'Zoom', 'StackScroll', 'Length', 'EllipticalROI', 'Probe'],
    panels: ['clinical', 'ai', 'render', 'measure', 'report', 'seg'],
    clinicalTools: ['runAgatstonScore', 'runCTR', 'runCardiacSegmentation', 'runAortaDiameter'],
    default3DPreset: 'ct-cardiac',
    defaultViewMode: 'mpr',
    autoPreset: 'ct_angio',
  },

  pulmonology: {
    id: 'pulmonology',
    name: 'Пульмонология',
    icon: '🫁',
    description: 'Лёгкие, эмфизема, пневмоторакс, нодулы',
    defaultPresets: ['ct_lung', 'ct_chest', 'ct_mediastinum', 'ct_pe', 'ct_soft_tissue'],
    quickTools: ['WindowLevel', 'Pan', 'Zoom', 'StackScroll', 'Length', 'EllipticalROI', 'RectangleROI'],
    panels: ['clinical', 'ai', 'measure', 'render', 'report', 'seg'],
    clinicalTools: ['runEmphysemaQuantification', 'runPneumothoraxDetection', 'runCTR'],
    default3DPreset: 'ct-lung-airways',
    defaultViewMode: 'mpr',
    autoPreset: 'ct_lung',
  },

  neurology: {
    id: 'neurology',
    name: 'Нейрохирургия',
    icon: '🧠',
    description: 'Мозг, инсульт, субдуральные гематомы, височная кость',
    defaultPresets: ['ct_brain', 'ct_stroke', 'ct_subdural', 'ct_temporal_bone', 'ct_bone'],
    quickTools: ['WindowLevel', 'Pan', 'Zoom', 'StackScroll', 'Length', 'Angle', 'EllipticalROI'],
    panels: ['ai', 'measure', 'clinical', 'render', 'report', 'seg'],
    clinicalTools: ['runFractureDetection', 'runRegionGrow3D'],
    default3DPreset: 'ct-brain-tissue',
    defaultViewMode: 'axial',
    autoPreset: 'ct_brain',
  },

  orthopedics: {
    id: 'orthopedics',
    name: 'Ортопедия',
    icon: '🦴',
    description: 'Кости, переломы, позвоночник, BMD',
    defaultPresets: ['ct_bone', 'ct_spine', 'ct_soft_tissue', 'ct_abdomen'],
    quickTools: ['WindowLevel', 'Pan', 'Zoom', 'StackScroll', 'Length', 'Angle', 'CobbAngle', 'Bidirectional'],
    panels: ['clinical', 'measure', 'render', 'ai', 'report'],
    clinicalTools: ['runBMD', 'runFractureDetection', 'runRegionGrow3D'],
    default3DPreset: 'ct-bone-clean',
    defaultViewMode: 'mpr',
    autoPreset: 'ct_bone',
  },

  vascular: {
    id: 'vascular',
    name: 'Сосудистая хирургия',
    icon: '🩸',
    description: 'Ангиография, аорта, аневризмы, стенозы',
    defaultPresets: ['ct_angio', 'ct_pe', 'ct_soft_tissue', 'ct_mediastinum', 'ct_abdomen'],
    quickTools: ['WindowLevel', 'Pan', 'Zoom', 'StackScroll', 'Length', 'EllipticalROI', 'Probe'],
    panels: ['clinical', 'render', 'ai', 'measure', 'report'],
    clinicalTools: ['runAortaDiameter', 'runAgatstonScore', 'runCTR'],
    default3DPreset: 'ct-angio-mip',
    defaultViewMode: 'mpr',
    autoPreset: 'ct_angio',
  },

  abdominal: {
    id: 'abdominal',
    name: 'Абдоминальная хирургия',
    icon: '🏥',
    description: 'Живот, печень, таз, онкология',
    defaultPresets: ['ct_abdomen', 'ct_liver', 'ct_pelvis', 'ct_soft_tissue', 'ct_angio'],
    quickTools: ['WindowLevel', 'Pan', 'Zoom', 'StackScroll', 'Length', 'EllipticalROI', 'RectangleROI', 'Bidirectional'],
    panels: ['ai', 'clinical', 'measure', 'seg', 'render', 'report'],
    clinicalTools: ['runRegionGrow3D', 'runAortaDiameter'],
    default3DPreset: 'ct-soft-tissue',
    defaultViewMode: 'mpr',
    autoPreset: 'ct_abdomen',
  },
};

// ══════════════════════════════════════════════════════════════
//  STATE
// ══════════════════════════════════════════════════════════════

let _activeProfile = PROFILES.general;
let _onChangeCallbacks = [];

// ══════════════════════════════════════════════════════════════
//  API
// ══════════════════════════════════════════════════════════════

/** Загрузить сохранённый профиль из DB */
export async function loadProfile() {
  try {
    const saved = await getUserPref('profile');
    if (saved && PROFILES[saved]) {
      _activeProfile = PROFILES[saved];
    }
  } catch (e) {
    log.warn('[Profile] Load failed:', e.message);
  }
  return _activeProfile;
}

/** Установить профиль + сохранить в DB */
export async function setProfile(profileId) {
  if (!PROFILES[profileId]) {
    log.warn('[Profile] Unknown profile:', profileId);
    return false;
  }
  _activeProfile = PROFILES[profileId];
  try {
    await setUserPref('profile', profileId);
  } catch (e) {
    log.warn('[Profile] Save failed:', e.message);
  }
  log.debug(`[Profile] Switched to: ${_activeProfile.name} (${_activeProfile.icon})`);
  _onChangeCallbacks.forEach(fn => { try { fn(_activeProfile); } catch (e) { log.debug('[ProfileManager]', e.message); } });
  return true;
}

/** Текущий профиль */
export function getProfile() {
  return _activeProfile;
}

/** Все профили */
export function getAllProfiles() {
  return PROFILES;
}

/** Подписка на смену профиля */
export function onProfileChange(fn) {
  _onChangeCallbacks.push(fn);
}

// ══════════════════════════════════════════════════════════════
//  ПРИМЕНЕНИЕ ПРОФИЛЯ К UI
// ══════════════════════════════════════════════════════════════

/**
 * Применить текущий профиль к интерфейсу:
 * 1. Сортировка W/L пресетов (приоритетные наверх)
 * 2. Подсветка рекомендованных инструментов
 * 3. Сортировка вкладок панелей
 * 4. Скрытие нерелевантных клинических инструментов
 */
export function applyProfileToUI() {
  const p = _activeProfile;

  // 1. Пресеты: выделяем приоритетные
  _sortPresetChips(p.defaultPresets);

  // 2. Мобильные инструменты: выделяем быстрые
  _highlightQuickTools(p.quickTools);

  // 3. Панели: сортировка вкладок
  _sortPanelTabs(p.panels);

  // 4. Клинические инструменты: показать/скрыть
  _filterClinicalTools(p.clinicalTools);

  // 5. Индикатор профиля
  _showProfileIndicator(p);

  log.debug(`[Profile] UI applied: ${p.name}`);
}

/** Сортировка preset-chip в sidebar по приоритету профиля */
function _sortPresetChips(priorityPresets) {
  const container = document.getElementById('presets-list') ||
    document.querySelector('.preset-chips');
  if (!container) return;

  const chips = Array.from(container.querySelectorAll('.preset-chip[data-preset]'));
  if (!chips.length) return;

  // Добавляем badge приоритетным
  chips.forEach(chip => {
    const presetId = chip.dataset.preset || chip.dataset.ww;
    chip.classList.remove('profile-priority');
    if (priorityPresets.some(p => presetId && presetId.includes(p))) {
      chip.classList.add('profile-priority');
    }
  });

  // Сортируем: приоритетные наверх
  const sorted = chips.sort((a, b) => {
    const aP = a.classList.contains('profile-priority') ? 0 : 1;
    const bP = b.classList.contains('profile-priority') ? 0 : 1;
    return aP - bP;
  });
  sorted.forEach(chip => container.appendChild(chip));
}

/** Выделение быстрых инструментов в мобильном drawer */
function _highlightQuickTools(quickTools) {
  document.querySelectorAll('#mobile-tools-drawer .m-tool-btn[data-tool]').forEach(btn => {
    const tool = btn.dataset.tool;
    btn.classList.toggle('profile-quick', quickTools.includes(tool));
    // Порядок: quickTools наверх
    btn.style.order = quickTools.indexOf(tool) >= 0 ? quickTools.indexOf(tool) : 99;
  });

  // Desktop toolbar: те же маркеры
  document.querySelectorAll('.tool-btn[data-tool]').forEach(btn => {
    btn.classList.toggle('profile-quick', quickTools.includes(btn.dataset.tool));
  });
}

/** Сортировка вкладок в мобильном panels drawer */
function _sortPanelTabs(panelOrder) {
  const tabContainer = document.querySelector('#mobile-panels-drawer .drawer-content > div:first-child');
  if (!tabContainer) return;

  const tabs = Array.from(tabContainer.querySelectorAll('[id^="mpanel-tab-"]'));
  if (!tabs.length) return;

  tabs.sort((a, b) => {
    const aId = a.id.replace('mpanel-tab-', '');
    const bId = b.id.replace('mpanel-tab-', '');
    const aIdx = panelOrder.indexOf(aId);
    const bIdx = panelOrder.indexOf(bId);
    return (aIdx < 0 ? 99 : aIdx) - (bIdx < 0 ? 99 : bIdx);
  });
  tabs.forEach(tab => tabContainer.appendChild(tab));
}

/** Фильтрация клинических инструментов */
function _filterClinicalTools(allowedTools) {
  // В панели clinical — показать только релевантные кнопки
  document.querySelectorAll('[data-clinical-tool]').forEach(btn => {
    const tool = btn.dataset.clinicalTool;
    if (tool) {
      btn.style.display = allowedTools.includes(tool) ? '' : 'none';
    }
  });
}

/** Индикатор текущего профиля */
function _showProfileIndicator(profile) {
  // Desktop: обновляем существующий btn-profile (без создания дубликата)
  const btnProfile = document.getElementById('btn-profile');
  if (btnProfile) {
    btnProfile.innerHTML = `<svg class="ico"><use href="#i-user"/></svg> ${escapeHtml(profile.name)}`;
    btnProfile.title = `Профиль: ${profile.name}`;
  }

  // Mobile: обновляем существующий btn-profile-m
  const mBtn = document.getElementById('btn-profile-m');
  if (mBtn) {
    mBtn.innerHTML = `<span class="mtop-icon"><svg class="ico"><use href="#i-user"/></svg></span>${escapeHtml(profile.name)}`;
  }
}

// ══════════════════════════════════════════════════════════════
//  МОДАЛЬНОЕ ОКНО ВЫБОРА ПРОФИЛЯ
// ══════════════════════════════════════════════════════════════

export function showProfileSelector() {
  // Удаляем старое если есть
  const old = document.getElementById('profile-selector-modal');
  if (old) old.remove();

  const modal = document.createElement('div');
  modal.id = 'profile-selector-modal';
  modal.style.cssText = `
    position:fixed; top:0; left:0; width:100%; height:100%;
    background:rgba(0,0,0,0.7); z-index:10000;
    display:flex; align-items:center; justify-content:center;
    backdrop-filter:blur(4px);
  `;

  const card = document.createElement('div');
  card.style.cssText = `
    background:var(--bg-panel,#1a1a2e); border-radius:16px;
    padding:24px; max-width:520px; width:90%;
    max-height:80vh; overflow-y:auto;
    box-shadow:0 20px 60px rgba(0,0,0,0.5);
  `;

  let html = `
    <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:16px">
      <h3 style="margin:0;color:var(--text,#fff);font-size:18px">👨‍⚕️ Профиль врача</h3>
      <button id="profile-close-btn" style="background:none;border:none;color:var(--text-dim,#888);font-size:22px;cursor:pointer">&times;</button>
    </div>
    <p style="color:var(--text-dim,#888);font-size:12px;margin:0 0 16px">
      Профиль настраивает пресеты, инструменты и панели под вашу специальность
    </p>
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px">
  `;

  for (const [id, p] of Object.entries(PROFILES)) {
    const isActive = id === _activeProfile.id;
    html += `
      <button class="profile-card" data-profile="${id}" style="
        background:${isActive ? 'var(--accent,#3b82f6)' : 'var(--bg-input,#252540)'};
        border:2px solid ${isActive ? 'var(--accent,#3b82f6)' : 'transparent'};
        border-radius:12px; padding:14px 12px; cursor:pointer;
        text-align:center; transition:all 0.2s;
        color:var(--text,#fff);
      ">
        <div style="font-size:28px;margin-bottom:6px">${p.icon}</div>
        <div style="font-size:13px;font-weight:600">${p.name}</div>
        <div style="font-size:10px;color:${isActive ? 'rgba(255,255,255,0.8)' : 'var(--text-dim,#888)'};margin-top:4px;line-height:1.3">${p.description}</div>
      </button>
    `;
  }
  // [11.1.4] АВТОРЕЖИМ — ВОСЬМАЯ КАРТОЧКА, а не строка с галочкой внизу.
  //
  // Решение владельца, и оно верное по сути: авторежим — это ВЫБОР НАРАВНЕ с
  // семью профилями («пусть решает программа»), а не примечание под ними.
  // Строкой с галочкой он выглядел настройкой второго сорта, и его пропускали.
  //
  // Карточка показывает СОСТОЯНИЕ: включён — подсвечена, как активный профиль.
  // Щелчок переключает. Правило работы описано прямо в карточке, чтобы не
  // гадать, во что именно переключится программа.
  const автоВкл = _autoSwitchEnabled;
  html += `
    <button class="profile-card profile-auto-card" id="profile-auto-modality" data-auto="1" style="
      background:${автоВкл ? 'var(--accent,#3b82f6)' : 'var(--bg-input,#252540)'};
      border:2px solid ${автоВкл ? 'var(--accent,#3b82f6)' : 'transparent'};
      border-radius:12px; padding:14px 12px; cursor:pointer;
      text-align:center; transition:all 0.2s; color:var(--text,#fff);
    " title="Программа сама выбирает профиль по модальности открытого исследования. Ваш выбор не отменяется: если профиль уже задан вручную, авторежим его не трогает">
      <div style="font-size:28px;margin-bottom:6px">🎯</div>
      <div style="font-size:13px;font-weight:600">Авторежим${автоВкл ? ' · вкл' : ''}</div>
      <div style="font-size:10px;color:${автоВкл ? 'rgba(255,255,255,0.8)' : 'var(--text-dim,#888)'};margin-top:4px;line-height:1.3">
        МР — нейрохирургия, УЗИ — абдоминальная, рентген — ортопедия, КТ — общий
      </div>
    </button>
  `;
  html += '</div>';
  card.innerHTML = html;
  modal.appendChild(card);
  document.body.appendChild(modal);

  // Events
  modal.addEventListener('click', (e) => {
    if (e.target === modal) modal.remove();
  }, { signal: _ac.signal });
  card.querySelector('#profile-close-btn').addEventListener('click', () => modal.remove(), { signal: _ac.signal });

  // [11.1.4] Авторежим — карточка-переключатель, а не чекбокс: слушаем click.
  card.querySelector('#profile-auto-modality')?.addEventListener('click', (e) => {
    e.stopPropagation();                       // не перепутать с выбором профиля
    const станет = !_autoSwitchEnabled;
    setAutoSwitchByModality(станет);
    if (typeof window.showToast === 'function') {
      window.showToast(станет
        ? 'Авторежим включён: профиль будет меняться по модальности исследования'
        : 'Авторежим выключен: профиль остаётся тем, что выбран вручную', 'info', 2500);
    }
    modal.remove();
    showProfileSelector();                     // перерисовать — карточка покажет новое состояние
  }, { signal: _ac.signal });

  // [11.1.4] Карточку авторежима сюда НЕ пускаем: у неё нет data-profile, и
  // общий обработчик упал бы на PROFILES[undefined].icon. Селектор с
  // :not([data-auto]) — а не проверка внутри — чтобы слушатель на неё вообще
  // не вешался: два слушателя на одну кнопку мы весь месяц выкорчёвывали.
  card.querySelectorAll('.profile-card:not([data-auto])').forEach(btn => {
    btn.addEventListener('click', async () => {
      const id = btn.dataset.profile;
      if (!id || !PROFILES[id]) return;
      await setProfile(id);
      applyProfileToUI();
      modal.remove();
      if (typeof window.showToast === 'function') {
        window.showToast(`${PROFILES[id].icon} Профиль: ${PROFILES[id].name}`, 'success', 2000);
      }
    }, { signal: _ac.signal });
  });
}

// Глобальный доступ для onclick
window._showProfileSelector = showProfileSelector;

// ══════════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════
// ENTERPRISE FEATURES (v6) — Custom profiles, auto-switch, import/export
// ══════════════════════════════════════════════════════════════

const CUSTOM_PROFILES_KEY = 'mikhmel_custom_profiles';
// [11.1.4] Состояние авторежима ПЕРЕЖИВАЕТ перезагрузку. Прежде флаг жил
// только в памяти: врач включал авторежим, закрывал программу — и назавтра он
// был выключен, молча. Впрочем, заметить это было нельзя: авторежим до этой
// версии не работал вовсе.
let _autoSwitchEnabled = (() => {
  try { return localStorage.getItem('mikhmel-profile-auto') === '1'; } catch (e) { return false; }
})();

/**
 * Create and save a custom profile
 * @param {Object} profile — must have id, name, icon, description + arrays
 */
export function createCustomProfile(profile) {
  if (!profile.id || !profile.name) return;
  const customs = _getCustomProfiles();
  customs[profile.id] = { ...PROFILES.general, ...profile, custom: true };
  PROFILES[profile.id] = customs[profile.id];
  try { localStorage.setItem(CUSTOM_PROFILES_KEY, JSON.stringify(customs)); } catch { /* non-critical */ }
  log.debug(`[Profile] Custom created: ${profile.name}`);
}

/**
 * Delete a custom profile
 * @param {string} profileId
 */
export function deleteCustomProfile(profileId) {
  const customs = _getCustomProfiles();
  delete customs[profileId];
  delete PROFILES[profileId];
  try { localStorage.setItem(CUSTOM_PROFILES_KEY, JSON.stringify(customs)); } catch { /* non-critical */ }
}

/**
 * List custom profiles
 * @returns {Array}
 */
export function listCustomProfiles() {
  return Object.values(_getCustomProfiles());
}

function _getCustomProfiles() {
  try { return JSON.parse(localStorage.getItem(CUSTOM_PROFILES_KEY) || '{}'); } catch { return {}; }
}

// Load custom profiles on module init
try {
  const customs = _getCustomProfiles();
  for (const [id, p] of Object.entries(customs)) PROFILES[id] = p;
} catch (e) { log.debug("[profileManager]", e.message); }

/**
 * Enable auto-switch profile by DICOM modality
 * When a series is loaded, profile switches automatically
 * @param {boolean} enabled
 */
export function setAutoSwitchByModality(enabled) {
  _autoSwitchEnabled = enabled;
  try { localStorage.setItem('mikhmel-profile-auto', enabled ? '1' : '0'); } catch (e) { /* приватный режим */ }
  log.debug(`[Profile] Auto-switch by modality: ${enabled}`);
}

export function isAutoSwitchEnabled() { return _autoSwitchEnabled; }

/**
 * [11.1.4] АВТОПЕРЕКЛЮЧЕНИЕ ПРОФИЛЯ — теперь оно действительно работает.
 *
 * ПОВОД — прямая жалоба владельца: «установка галочки не срабатывает, я
 * открываю МРТ головного мозга, а профиль остаётся общим».
 *
 * Он был прав, и причина оказалась хрестоматийной для этой программы:
 * `setAutoSwitchByModality` ЗАПИСЫВАЛА ФЛАГ И БОЛЬШЕ НИЧЕГО. Переменную
 * `_autoSwitchEnabled` не читал никто, кроме отчёта о состоянии и самой
 * галочки при перерисовке. Функция `detectProfileForModality` существовала и
 * не вызывалась НИ ОТКУДА. То есть галочка была вывеской: ставилась,
 * сохранялась, показывалась — и не значила ничего.
 *
 * Теперь: программа зовёт `автопрофильПоМодальности(модальность)` при
 * загрузке серии, и профиль меняется на деле.
 *
 * Два правила, без которых вышло бы хуже, чем ничего:
 *   • переключаем ТОЛЬКО с «Общего». Если врач сам выбрал кардиологию и открыл
 *     МРТ, отбирать его выбор нельзя — это его решение, а не наше;
 *   • ОТЧИТЫВАЕМСЯ на экране. Молчаливая смена профиля меняет пресеты и набор
 *     инструментов; врач должен видеть, что это сделала программа, а не он.
 *
 * @param {string} модальность — 'CT', 'MR', 'US', 'CR'/'DX'…
 * @returns {string|null} id профиля, если переключились
 */
export async function автопрофильПоМодальности(модальность) {
  if (!_autoSwitchEnabled) return null;
  const цель = detectProfileForModality(модальность);
  if (!цель) return null;
  if (_activeProfile?.id === цель) return null;             // уже нужный
  if (_activeProfile?.id && _activeProfile.id !== 'general') return null;  // выбор врача не трогаем

  await setProfile(цель);
  applyProfileToUI();
  const имя = PROFILES[цель]?.name || цель;
  if (typeof window.showToast === 'function') window.showToast(`Профиль переключён на «${имя}» по модальности ${модальность}. Сменить — кнопка «Профиль врача»`, 'info', 4000);
  log.info(`[Профиль] авто: ${модальность} → ${цель}`);
  return цель;
}

/**
 * Auto-detect best profile for modality
 * @param {string} modality — e.g. 'CT', 'MR'
 * @returns {string|null} profile ID
 */
export function detectProfileForModality(modality) {
  const mod = (modality || '').toUpperCase();
  // [11.1.4] Добавлены CR и DX — так рентген и обозначается в DICOM. 'XR' в
  // стандарте нет вовсе: строка стояла в карте, но совпасть не могла никогда.
  // Пока автопереключение было вывеской, ошибку нельзя было заметить.
  const map = {
    CT: 'general', MR: 'neurology', US: 'abdominal',
    CR: 'orthopedics', DX: 'orthopedics', RG: 'orthopedics', XR: 'orthopedics',
    MG: 'general', PT: 'general', NM: 'general',
  };
  return map[mod] || null;
}

/**
 * Import profile from JSON string
 * @param {string} json
 */
export function importProfileJSON(json) {
  try {
    const p = JSON.parse(json);
    if (p.id && p.name) { createCustomProfile(p); return true; }
  } catch (e) { log.debug("[profileManager]", e.message); }
  return false;
}

/**
 * Export current profile as JSON
 * @returns {string}
 */
export function exportCurrentProfileJSON() { return JSON.stringify(_activeProfile, null, 2); }

export async function resetProfile() { await setProfile('general'); applyProfileToUI(); }
export function getProfileStats() {
  return { activeId: _activeProfile.id, activeName: _activeProfile.name, total: Object.keys(PROFILES).length,
    custom: listCustomProfiles().length, autoSwitch: _autoSwitchEnabled };
}
export function exportAllProfiles() {
  const blob = new Blob([JSON.stringify(PROFILES, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `profiles_${Date.now()}.json`; a.click(); URL.revokeObjectURL(url);
}
export function getQuickTools() { return [...(_activeProfile.quickTools || [])]; }
export function getClinicalTools() { return [...(_activeProfile.clinicalTools || [])]; }

// [FIX] Cleanup all event listeners
export function destroyProfileManager() {
  _ac.abort();
  _ac = new AbortController();
}
